<template>
  <div class="shop-in-shop-panel">
    <template v-if="!storeCd">
      <el-alert :title="$t('매장을 먼저 저장한 뒤 묶음을 구성할 수 있습니다.')" type="info" :closable="false" />
    </template>

    <template v-else>
      <!-- 그룹 현황 -->
      <h4>{{ $t('매장 묶음') }}</h4>
      <p class="hint">
        {{
          $t(
            '한 주방의 매장을 대표 1곳과 형제 매장으로 묶습니다. 멀티 사업자 묶음은 대표 계정이 전체를 관리하고, 형제 계정은 자기 매장만 관리합니다.'
          )
        }}
      </p>

      <el-form v-if="attributesEditable" inline size="small" @submit.native.prevent>
        <el-form-item :label="$t('묶음 종류')">
          <el-select v-model="groupKind" :disabled="loading">
            <el-option :label="$t('샵인샵')" value="SHOP_IN_SHOP" />
            <el-option :label="$t('멀티 사업자')" value="MULTI_BUSINESS" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" :loading="loading" @click="saveAttributes">{{ $t('묶음 설정 저장') }}</el-button>
        </el-form-item>
      </el-form>
      <p v-else-if="primary" class="hint">
        {{ $t(primary.groupKind === 'MULTI_BUSINESS' ? '멀티 사업자' : '샵인샵') }}
      </p>

      <el-table :data="group" size="small" border v-loading="loading" style="max-width: 720px">
        <el-table-column prop="storeCd" :label="$t('매장 코드')" width="90" />
        <el-table-column prop="storeId" :label="$t('매장 ID')" width="130" />
        <el-table-column prop="storeNm" :label="$t('매장명')" min-width="160" />
        <el-table-column :label="$t('주문번호 머리글자')" width="140">
          <template slot-scope="scope">
            <el-input
              v-if="attributesEditable"
              v-model="scope.row.prefixInput"
              :placeholder="scope.row.orderNumberPrefix"
              :disabled="loading"
              size="small" />
            <span v-else>{{ scope.row.orderNumberPrefix }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('구분')" width="90">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.isPrimary" type="danger" size="mini">{{ $t('대표') }}</el-tag>
            <el-tag v-else size="mini">{{ $t('형제') }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column :label="$t('관리')" width="200" v-if="configurationEditable">
          <template slot-scope="scope">
            <el-button
              v-if="!scope.row.isPrimary"
              size="mini"
              type="warning"
              plain
              @click="confirmSucceed(scope.row)"
              >{{ $t('대표 승계') }}</el-button
            >
            <el-button v-if="!scope.row.isPrimary" size="mini" type="danger" plain @click="confirmUnlink(scope.row)">{{
              $t('해제')
            }}</el-button>
          </template>
        </el-table-column>
      </el-table>
      <p v-if="attributesEditable" class="hint">
        {{
          $t(
            '머리글자를 비워 두면 매장 이름에서 겹치지 않게 정합니다. 직접 정할 때는 서로 다른 한 글자를 입력해 주세요.'
          )
        }}
      </p>

      <!-- 형제 연결 -->
      <div v-if="configurationEditable" class="link-form">
        <h4>{{ $t('형제 매장 연결') }}</h4>
        <el-form inline size="small" @submit.native.prevent>
          <el-form-item :label="$t('매장 코드')">
            <el-input
              v-model="linkTargetCd"
              :placeholder="$t('연결할 매장 코드')"
              style="width: 160px"
              @keyup.enter.native="previewLinkTarget" />
          </el-form-item>
          <el-form-item>
            <el-button icon="el-icon-search" @click="previewLinkTarget">{{ $t('조회') }}</el-button>
          </el-form-item>
          <el-form-item v-if="linkPreview">
            <el-tag>{{ linkPreview.storeNm }} (#{{ linkPreview.storeCd }})</el-tag>
            <el-button type="primary" icon="el-icon-link" @click="confirmLink">{{ $t('형제로 연결') }}</el-button>
          </el-form-item>
        </el-form>
        <p class="hint">
          {{ $t('* 연결 대상은 단독 매장(다른 그룹 미소속)이어야 하며, 현재 매장이 대표가 됩니다.') }}
        </p>
      </div>

      <!-- 그룹 매출 롤업 -->
      <div class="group-sales" v-if="group.length > 1">
        <h4>{{ $t('그룹 매출 (실결제 · 판매 마감시각 기준)') }}</h4>
        <el-form inline size="small" @submit.native.prevent>
          <el-form-item :label="$t('기간')">
            <el-date-picker
              v-model="salesRange"
              type="daterange"
              value-format="yyyy-MM-dd"
              :start-placeholder="$t('시작일')"
              :end-placeholder="$t('종료일')"
              style="width: 260px" />
          </el-form-item>
          <el-form-item>
            <el-button icon="el-icon-search" @click="loadGroupSales">{{ $t('조회') }}</el-button>
          </el-form-item>
        </el-form>
        <el-table :data="groupSales" size="small" border style="max-width: 560px" v-loading="salesLoading">
          <el-table-column prop="storeNm" :label="$t('매장명')" min-width="140" />
          <el-table-column :label="$t('구분')" width="80">
            <template slot-scope="scope">
              <el-tag v-if="scope.row.isPrimary" type="danger" size="mini">{{ $t('대표') }}</el-tag>
              <el-tag v-else size="mini">{{ $t('형제') }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="salesCnt" :label="$t('매출 건수')" width="100" align="right" />
          <el-table-column :label="$t('매출 금액')" width="140" align="right">
            <template slot-scope="scope">{{ formatMoney(scope.row.amount) }}</template>
          </el-table-column>
        </el-table>
        <p class="hint" v-if="groupSalesTotal !== null">
          {{ $t('그룹 합계') }}: <b>{{ formatMoney(groupSalesTotal) }}</b>
        </p>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'ShopInShopPanel',
  props: {
    storeCd: { type: Number, default: null },
    editable: { type: Boolean, default: false },
    attributesEditable: { type: Boolean, default: false },
  },
  data() {
    return {
      loading: false,
      group: [],
      groupKind: 'SHOP_IN_SHOP',
      linkTargetCd: '',
      linkPreview: null,
      salesRange: null,
      salesLoading: false,
      groupSales: [],
    }
  },
  computed: {
    configurationEditable() {
      return (
        this.editable && (!(this.primary && this.primary.groupKind === 'MULTI_BUSINESS') || this.attributesEditable)
      )
    },
    primary() {
      return this.group.find((m) => m.isPrimary) || null
    },
    groupSalesTotal() {
      if (!this.groupSales.length) return null
      return this.groupSales.reduce((sum, r) => sum + (r.amount || 0), 0)
    },
  },
  watch: {
    storeCd: {
      handler(val) {
        this.linkPreview = null
        this.linkTargetCd = ''
        this.groupSales = []
        if (val) this.loadGroup()
      },
      immediate: true,
    },
  },
  methods: {
    applyGroup(members) {
      this.group = (members || []).map((member) => ({
        ...member,
        prefixInput: member.orderNumberPrefixOverride || '',
      }))
      this.groupKind = (this.primary && this.primary.groupKind) || 'SHOP_IN_SHOP'
    },
    async saveAttributes() {
      this.loading = true
      try {
        const res = await this.$axios.post('/api/shop-in-shop/attributes', {
          storeCd: this.storeCd,
          groupKind: this.groupKind,
          prefixOverrides: Object.fromEntries(this.group.map((member) => [member.storeCd, member.prefixInput || null])),
        })
        this.applyGroup(res.data)
        this.$message.success(this.$t('묶음 설정을 저장했습니다.'))
      } catch (e) {
        this.$message.error(this.serverError(e))
      } finally {
        this.loading = false
      }
    },
    formatMoney(v) {
      return (v ?? 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },
    async loadGroup() {
      this.loading = true
      try {
        const res = await this.$axios.get(`/api/shop-in-shop/group/${this.storeCd}`)
        this.applyGroup(res.data)
      } catch (e) {
        this.group = []
        this.$message.error(this.$t('매장 묶음 조회에 실패했습니다.'))
      } finally {
        this.loading = false
      }
    },
    async previewLinkTarget() {
      const cd = parseInt(this.linkTargetCd, 10)
      if (!cd) {
        this.$message.warning(this.$t('매장 코드를 입력하십시오.'))
        return
      }
      try {
        const res = await this.$axios.get(`/api/shop-in-shop/group/${cd}`)
        const members = res.data || []
        const target = members.find((m) => m.storeCd === cd)
        if (!target) {
          this.$message.error(this.$t('매장을 찾을 수 없습니다.'))
          return
        }
        if (members.length > 1) {
          this.$message.warning(this.$t('이미 다른 샵인샵 그룹에 소속된 매장입니다.'))
          this.linkPreview = null
          return
        }
        this.linkPreview = target
      } catch (e) {
        this.$message.error(this.$t('매장을 찾을 수 없습니다.'))
        this.linkPreview = null
      }
    },
    confirmLink() {
      if (!this.linkPreview) return
      const primaryCd = this.primary ? this.primary.storeCd : this.storeCd
      this.$confirm(
        this.$t(
          this.primary && this.primary.groupKind === 'MULTI_BUSINESS'
            ? '선택한 매장을 대표 매장의 사업장으로 옮기고 멀티 사업자 묶음에 연결합니다. 계속하시겠습니까?'
            : '선택한 매장을 형제로 연결합니다. 그룹 전체(메뉴판·품절 공유)에 반영됩니다. 계속하시겠습니까?'
        ),
        this.$t('형제 연결'),
        { type: 'warning' }
      )
        .then(async () => {
          try {
            const res = await this.$axios.post('/api/shop-in-shop/link', {
              primaryStoreCd: primaryCd,
              siblingStoreCd: this.linkPreview.storeCd,
            })
            this.applyGroup(res.data)
            this.linkPreview = null
            this.linkTargetCd = ''
            this.$message.success(this.$t('형제 매장이 연결되었습니다.'))
          } catch (e) {
            this.$message.error(this.serverError(e))
          }
        })
        .catch(() => {})
    },
    confirmUnlink(row) {
      this.$confirm(
        this.$t('형제 연결을 해제합니다. 공유 메뉴 사본은 유지되지만 품절 공유가 중단됩니다. 계속하시겠습니까?'),
        this.$t('형제 해제'),
        { type: 'warning' }
      )
        .then(async () => {
          try {
            const res = await this.$axios.post('/api/shop-in-shop/unlink', { siblingStoreCd: row.storeCd })
            this.applyGroup(res.data)
            this.$message.success(this.$t('해제되었습니다.'))
          } catch (e) {
            this.$message.error(this.serverError(e))
          }
        })
        .catch(() => {})
    },
    confirmSucceed(row) {
      const currentPrimary = this.primary
      if (!currentPrimary) return
      this.$confirm(
        this.$t('대표 매장을 승계합니다. 그룹 전체에 반영되며 POS 재로그인이 필요합니다. 계속하시겠습니까?'),
        this.$t('대표 승계'),
        { type: 'warning' }
      )
        .then(async () => {
          try {
            const res = await this.$axios.post('/api/shop-in-shop/succeed', {
              currentPrimaryStoreCd: currentPrimary.storeCd,
              newPrimaryStoreCd: row.storeCd,
              idempotencyKey: `succeed-${currentPrimary.storeCd}-${row.storeCd}-${Date.now()}`,
            })
            this.applyGroup(res.data)
            this.$message.success(this.$t('대표가 승계되었습니다.'))
          } catch (e) {
            this.$message.error(this.serverError(e))
          }
        })
        .catch(() => {})
    },
    async loadGroupSales() {
      if (!this.salesRange || this.salesRange.length !== 2) {
        this.$message.warning(this.$t('기간을 선택하십시오.'))
        return
      }
      this.salesLoading = true
      try {
        const res = await this.$axios.get(`/api/shop-in-shop/group-sales/${this.storeCd}`, {
          params: { fromDate: this.salesRange[0], toDate: this.salesRange[1] },
        })
        this.groupSales = res.data || []
      } catch (e) {
        this.$message.error(this.$t('그룹 매출 조회에 실패했습니다.'))
      } finally {
        this.salesLoading = false
      }
    },
    serverError(e) {
      return (e.response && e.response.data && e.response.data.message) || this.$t('처리에 실패했습니다.')
    },
  },
}
</script>

<style scoped>
.shop-in-shop-panel h4 {
  margin: 16px 0 8px;
}
.shop-in-shop-panel .hint {
  color: #909399;
  font-size: 12px;
  margin: 4px 0 12px;
}
.link-form {
  margin-top: 24px;
}
.group-sales {
  margin-top: 24px;
}
</style>
